import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import Modal from "@mui/material/Modal";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import "dayjs/locale/nl";
import { getUserFromLocalStorage } from "hooks/LocalStorage";

import { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import * as Yup from "yup";

dayjs.extend(customParseFormat);

import Overview from "./steps/overview";
import PassengerDetails from "./steps/passengerDetails";
import SeatsMap from "./steps/seatsMap";

import { flightReverse, getValidateFlight, seatMap } from "api/Flights";
import { getMember } from "api/Member";
import useApi from "hooks/useApi";
import { CurrencyContext } from "../useContext";

import { countries } from "assets/data/countries";

import usePackageDeal from "hooks/usePackageDeal";
import { useTranslation } from "i18n";
import {
  getCombinationBreakdown,
  hasRoomTypePricing,
  roundPrice,
} from "utils/packageDealPrice";

import "./style.css";
import "./redesign.css";

//----------------------------------------------------------------

const Booking = ({}) => {
  const { t, locale } = useTranslation();
  let navigate = useNavigate();
  const { state } = useLocation();
  const steps = [
    t("booking.steps.search"),
    t("booking.steps.passengerDetails"),
    t("booking.steps.seatMap"),
    t("booking.steps.overviewPayment"),
  ];
  const getTravelerTypeLabel = (type) => t(`booking.travelerTypes.${type}`);
  const getRoomTypeLabel = (type) =>
    ["single", "double", "triple", "quad"].includes(type)
      ? t(`booking.labels.${type}`)
      : type;

  const [openPop, setOpenPop] = useState(false);

  const { currency } = useContext(CurrencyContext);
  const [errMsg, setErrorMsg] = useState("");
  const [checked, setChecked] = useState(false);
  const [seats, setSeats] = useState([]);
  const [code, setCode] = useState();
  const [activeStep, setActiveStep] = useState(1);
  const [paymentType, setPaymentType] = useState();
  const [expand, setExpand] = useState(false);
  const [loading, setLoading] = useState(false);
  const [value, setValue] = useState(0);
  const [reservedSeats, setReservedSeats] = useState([]);
  const [count, setCount] = useState(0);
  const [errModalMsg, setErrModalMsg] = useState(false);
  const [overviewData, setOverViewDate] = useState();
  const [values, setValues] = useState();
  const [travelers, setTraverls] = useState([]);
  const [reverseSeats, setReverseSeats] = useState([]);
  const [travelerIndex, setTravelerIndex] = useState(0);
  const getMemberInfo = useApi(getMember);
  const { deal: packageDeal } = usePackageDeal(state?.packageDealId);
  const expiryAcknowledgedRef = useRef({});

  const packagePriceBreakdown = (() => {
    if (
      !state?.packageDeal ||
      !packageDeal?.price ||
      !hasRoomTypePricing(packageDeal.price) ||
      !state?.packageRoomBreakdown
    )
      return null;
    const rb = state.packageRoomBreakdown;
    const rooms = [
      rb.single > 0 && { type: "single", count: rb.single },
      rb.double > 0 && { type: "double", count: rb.double },
      rb.triple > 0 && { type: "triple", count: rb.triple },
      rb.quad > 0 && { type: "quad", count: rb.quad },
    ].filter(Boolean);
    if (rooms.length === 0) return null;
    return getCombinationBreakdown(packageDeal.price, rooms, currency);
  })();

  const handleChange = () => {
    setChecked(!checked);
  };

  const handleChangeTab = (event, newValue) => {
    setValue(newValue);
    setTravelerIndex(0);
  };

  const phoneRegExp =
    /^(\+?\d{0,4})?\s?-?\s?(\(?\d{3}\)?)\s?-?\s?(\(?\d{3}\)?)\s?-?\s?(\(?\d{4}\)?)?$/;

  let travelersInit = {};
  let validateTravelers = {};

  const countryCodes = countries.map((country) => country.code); // Extracting country codes from the countries array

  state?.flight?.travelerPricings?.map((item, index) => {
    const initialValues = {
      ["firstName" + index]:
        index === 0 ? getMemberInfo?.data?.memberDetails?.firstName : "",
      ["lastName" + index]:
        index === 0 ? getMemberInfo?.data?.memberDetails?.lastName : "",

      ["dateOfBirthDay" + index]:
        index === 0 && getMemberInfo?.data?.memberDetails?.dateOfBirth
          ? dayjs(getMemberInfo?.data?.memberDetails?.dateOfBirth).format("DD")
          : "",
      ["dateOfBirthMonth" + index]:
        index === 0 && getMemberInfo?.data?.memberDetails?.dateOfBirth
          ? dayjs(getMemberInfo?.data?.memberDetails?.dateOfBirth).format("MM")
          : "",
      ["expDateDay" + index]: "",
      ["expDateMonth" + index]: "",
      ["expDateYear" + index]: "",
      ["docNumber" + index]: "",
      ["countryCallingCode" + index]: "",
      ["documents" + index]: "",
      ["dateOfBirthYear" + index]:
        index === 0 && getMemberInfo?.data?.memberDetails?.dateOfBirth
          ? dayjs(getMemberInfo?.data?.memberDetails?.dateOfBirth).format(
              "YYYY",
            )
          : "",
      ["gender" + index]: "",
      ...(item.travelerType === "ADULT" && {
        ["emailAddress" + index]:
          state?.source === "NDC"
            ? "satyam@satyamholidays.net"
            : index === 0
              ? getMemberInfo?.data?.memberDetails?.email
              : "",
      }),
      ...(item.travelerType === "ADULT" && {
        ["number" + index]: "",
      }),
    };
    const validateValues = {
      ["firstName" + index]: Yup.string()
        .matches(/^[a-zA-Z\s]*$/g, t("booking.validation.lettersOnly"))
        .required()
        .label(t("booking.labels.firstName")),
      ["lastName" + index]: Yup.string()
        .required()
        .matches(/^[a-zA-Z\s]*$/g, t("booking.validation.lettersOnly"))
        .label(t("booking.labels.lastName")),
      ["expDateDay" + index]: Yup.number()
        .nullable()
        .max(31)
        .required()
        .label(t("booking.labels.passportExpirationDate")),
      ["expDateMonth" + index]: Yup.string()
        .nullable()
        .required()
        .label(t("booking.labels.passportExpirationDate")),
      ["expDateYear" + index]: Yup.string()
        .nullable()
        .required(t("booking.validation.expirationRequired"))
        .test(
          "is-valid-expiration-date",
          t("booking.validation.expirationInvalid"),
          (value, context) => {
            if (expiryAcknowledgedRef.current[index]) {
              return true;
            }

            const deDate =
              state?.flight?.itineraries?.[0]?.segments?.[0]?.departure?.at;

            if (!deDate) {
              return context.createError({
                message: t("booking.messages.expirationMissing"),
              });
            }

            const day = context.parent["expDateDay" + index];
            const month = context.parent["expDateMonth" + index];

            if (!value || !day || !month) {
              return context.createError({
                message: t("booking.messages.expirationIncomplete"),
              });
            }

            // Convert month name (January) to number (01) if needed
            const monthNum =
              monthsLong[month] || String(month).padStart(2, "0");
            const dayPadded = String(day).padStart(2, "0");

            // Construct the expiration date using day, month, and year
            const expirationDate = dayjs(
              `${monthNum}-${dayPadded}-${value}`,
              "MM-DD-YYYY",
              true,
            );
            const departureDate = dayjs(deDate);

            // Check if the expiration date is valid
            if (!expirationDate.isValid()) {
              return context.createError({
                message: t("booking.messages.expirationInvalidFormat"),
              });
            }

            // Ensure expiration date is at least 6 months after departure date (same day or later is valid)
            return !expirationDate.isBefore(departureDate.add(6, "months"));
          },
        ),

      ["docNumber" + index]: Yup.string().label(
        t("booking.labels.passportNumber"),
      ),
      ["countryCallingCode" + index]: Yup.string(),

      ["documents" + index]: Yup.array()

        .of(
          Yup.object().shape({
            nationality: Yup.string()
              .required()
              .label(t("booking.labels.nationality")),
            issuanceCountry: Yup.string()
              .required()
              .test(
                "match-country-code",
                t("booking.validation.invalidIssuanceCountry"),
                (value) => {
                  return countryCodes.includes(value);
                },
              )
              .label(t("booking.labels.issuanceCountry")),
          }),
        )
        .when('["documents" + index]', {
          is: (documents) => documents && documents.length > 0,
          then: Yup.array().required(t("booking.validation.documentsRequired")),
        }),

      ["dateOfBirthDay" + index]: Yup.number()
        .nullable()
        .max(31)
        .required()
        .label(t("booking.labels.dateOfBirth")),
      ["dateOfBirthMonth" + index]: Yup.string()
        .nullable()
        .required()
        .label(t("booking.labels.dateOfBirth")),
      ["dateOfBirthYear" + index]: Yup.string()
        .min(4)
        .max(4)
        .required()
        .label(t("booking.labels.dateOfBirth")),
      ["gender" + index]: Yup.string()
        .required()
        .label(t("booking.labels.gender")),
      ...(item.travelerType !== "CHILD" &&
        item.travelerType !== "HELD_INFANT" && {
          ["emailAddress" + index]: Yup.string()
            .email()
            .required()
            .label(t("booking.labels.email")),
        }),
      ...(item.travelerType !== "CHILD" &&
        item.travelerType !== "HELD_INFANT" && {
          ["number" + index]: Yup.string()
            .required()
            .matches(phoneRegExp, t("booking.validation.phoneInvalid"))
            .label(t("booking.labels.phoneNumber")),
        }),
    };
    travelersInit = { ...travelersInit, ...initialValues };
    validateTravelers = { ...validateTravelers, ...validateValues };
  });

  const validationSchema = Yup.object().shape(validateTravelers);

  const monthsLong = {
    January: "01",
    February: "02",
    March: "03",
    April: "04",
    May: "05",
    June: "06",
    July: "07",
    August: "08",
    September: "09",
    October: "10",
    November: "11",
    December: "12",
  };

  const handelInfoSubmit = (values) => {
    setValues(values);
    setActiveStep(2);

    navigate("/booking/seats-map", {
      state,
    });

    window.scrollTo(0, 0);
    const travelersData = [];
    state?.flight?.travelerPricings?.map((item, index) => {
      const userContact = {
        ...(item.travelerType !== "CHILD" &&
          item.travelerType !== "HELD_INFANT" && {
            contact: {
              emailAddress: values["emailAddress" + index],
              phones: [
                {
                  countryCallingCode: values[
                    "countryCallingCode" + index
                  ].replace("+", ""),
                  deviceType: "MOBILE",
                  number: values["number" + index].replace("+", ""),
                },
              ],
            },
          }),
        ...(values["documents" + index] && {
          documents: values["documents" + index],
        }),
        dateOfBirth:
          values["dateOfBirthYear" + index] +
          "-" +
          (values["dateOfBirthMonth" + index]?.length === 2
            ? values["dateOfBirthMonth" + index]
            : monthsLong[values["dateOfBirthMonth" + index]]) +
          "-" +
          ((values["dateOfBirthDay" + index] + "").length === 1
            ? "0" + values["dateOfBirthDay" + index]
            : values["dateOfBirthDay" + index]),
        gender: values["gender" + index].toUpperCase(),
        id: index + 1,
        name: {
          firstName: values["firstName" + index].toUpperCase(),
          lastName: values["lastName" + index].toUpperCase(),
        },
      };
      travelersData.push(userContact);
    });

    setTraverls(travelersData);
    const travelersId = [];
    travelersData?.map((i) => {
      const obj = {
        [i.id]: {
          seats: [],
        },
      };
      travelersId.push(obj);
    });

    setReverseSeats(travelersId);
  };

  //Flight Offer
  const handelSubmit = async () => {
    setErrModalMsg(false);
    setOpenPop(true);

    let flightInfo = state.flight;

    flightInfo.travelerPricings.map((segment, index) => {
      segment.fareDetailsBySegment?.map((fare) => {
        if (reverseSeats[index][segment?.travelerId]) {
          const getSeatNum = reverseSeats[index][
            segment?.travelerId
          ]?.seats?.find((i) => i?.segmentId === fare.segmentId);
          if (getSeatNum) {
            fare.additionalServices = {
              chargeableSeatNumber: getSeatNum?.selectedSeat,
            };
          } else {
            delete fare.additionalServices;
          }
        }
      });
    });

    const body = {
      schema: state?.ndcResponse ?? flightInfo,
      ...(state?.officeId && { officeId: state?.officeId }),
      isPrivateFare: false,
    };

    const res = await getValidateFlight({
      body,
      amaRefId: state?.amaRefId,
    });

    if (!res.ok) {
      setErrModalMsg(res?.data?.errorDetails?.errors);
      setTimeout(() => {
        setOpenPop(false);
      }, 4000);
    } else {
      setOverViewDate(res?.data?.response);
      setActiveStep(3);
      navigate("/booking/overview", {
        state,
      });
      window.scrollTo(0, 0);
      setOpenPop(false);
    }
  };

  // Order Create
  const handelReverseSubmit = async () => {
    setErrModalMsg(false);
    setOpenPop(true);

    let editGrandPrice = overviewData.data.flightOffers[0];
    editGrandPrice.price.grandTotal =
      getTotalPrice() ?? state?.flight?.price?.total;
    const body = {
      travelers,
      schema: state?.ndcResponse ?? editGrandPrice,
      adjustedPrice:
        travelers?.length > 0 && reverseSeats?.length > 0
          ? getTotalPrice()
          : state.priceWithFare,
      ...(state?.officeId && { officeId: state?.officeId }),
      ...(state?.packageDeal &&
        state?.packagePrice != null && {
          packagePrice: roundPrice(state.packagePrice),
          ...(state?.packageDealId && { packageDealId: state.packageDealId }),
          ...(state?.packageRoomBreakdown && {
            packageRoomBreakdown: state.packageRoomBreakdown,
          }),
        }),
    };

    const flightReverseResponse = await flightReverse({
      body,
      amaRefId: state?.amaRefId,
    });

    if (!flightReverseResponse.ok) {
      setErrModalMsg(flightReverseResponse?.data?.errorDetails?.errors);

      setTimeout(() => {
        setOpenPop(false);
        navigate(-3);
      }, 4000);
    } else {
      setOpenPop(false);

      const overviewData = state?.flight;
      overviewData.travelers =
        flightReverseResponse?.data?.response?.data.travelers;
      const flightTotal = getTotalPrice();
      const packageAdd =
        state?.packageDeal && state?.packagePrice != null
          ? roundPrice(state.packagePrice)
          : 0;
      const finalTotal = roundPrice(
        (parseFloat(flightTotal) || 0) + packageAdd,
      );

      navigate("/confirmation", {
        state: {
          overview: flightReverseResponse?.data?.response,
          travelerPricing: state?.flight?.travelerPricings,
          dictionaries: state?.dictionaries,
          fareAdjustment: state?.fareAdjustment,
          prevPath: state?.prevPath,
          reverseSeats: reverseSeats,
          getTotalPrice: finalTotal,
          tikkieUrl: flightReverseResponse?.data?.tikkieUrl,
          ...(state?.packageDeal && {
            packageDeal: true,
            packagePrice: roundPrice(state.packagePrice),
            ...(state?.packageDealId && { packageDealId: state.packageDealId }),
            ...(state?.packageRoomBreakdown && {
              packageRoomBreakdown: state.packageRoomBreakdown,
            }),
          }),
          ...(state?.officeId && { officeId: state?.officeId }),
        },
      });

      window.scrollTo(0, 0);
    }
  };

  const handelSeatMap = async () => {
    setLoading(true);

    const body = {
      schema: state?.ndcResponse ?? state?.flight,
      ...(state?.officeId && { officeId: state?.officeId }),
    };

    const res = await seatMap({
      body,
      amaRefId: state?.amaRefId,
    });

    if (!res.ok) {
      setErrorMsg(t("booking.messages.seatMapUnavailable"));
    } else {
      setSeats(res?.data?.response?.data);
      const allSegments = [];
      state.flight?.itineraries?.map((i) =>
        i?.segments?.map((seg) => {
          let arr = seg?.departure?.iataCode + seg?.arrival?.iataCode;
          arr = [];
          allSegments.push(arr);
        }),
      );

      setReservedSeats(allSegments);
    }
    setLoading(false);
  };

  const handelTraverlsSeats = (selectedSeat, price, checkIfExist) => {
    if (checkIfExist) {
      let indexTra;
      travelers?.map((traveler, index) => {
        reverseSeats[index][traveler?.id]?.seats?.map((i) => {
          if (
            i?.segmentId === seats[value]?.segmentId &&
            selectedSeat === i?.selectedSeat
          ) {
            indexTra = index;
          }
        });
      });

      handelRemoveSeat(selectedSeat, indexTra);
    } else {
      if (travelerIndex < travelers?.length - 0) {
        let data = [...reverseSeats];

        let getTraverlSeats =
          data[travelerIndex][travelers[travelerIndex].id].seats;

        const editSegmant = getTraverlSeats?.find(
          (i) => i.segmentId === seats[value]?.segmentId,
        );
        if (editSegmant) {
          if (
            travelers.length === 1 ||
            state?.flight?.travelerPricings?.filter(
              (trav) => trav?.travelerType !== "HELD_INFANT",
            )?.length === 1
          ) {
            editSegmant.selectedSeat = selectedSeat;
            editSegmant.price = price;
          } else {
            return false;
          }
        } else {
          const seat = {
            segmentId: seats[value]?.segmentId,
            selectedSeat,
            price,
          };
          getTraverlSeats.push(seat);
        }

        data[travelerIndex][travelers[travelerIndex].id].seats =
          getTraverlSeats;
        setReservedSeats(data);
        setCount(count + 1);
        if (
          travelerIndex <
          state?.flight?.travelerPricings?.filter(
            (trav) => trav?.travelerType !== "HELD_INFANT",
          )?.length -
            1
        ) {
          setTravelerIndex(travelerIndex + 1);
        }
      }
    }
  };

  const handelRemoveSeat = (selectedSeat, index) => {
    let data = [...reservedSeats];

    const editSegmant = data[index][travelers[index].id].seats?.filter(
      (i) => i.selectedSeat !== selectedSeat,
    );

    data[index][travelers[index].id].seats = editSegmant;
    setReservedSeats(data);
    setCount(count + 1);
    setTravelerIndex(index);
  };

  const getTotalPrice = () => {
    let arr = [];

    travelers?.map((traveler, index) => {
      reverseSeats[index][traveler?.id]?.seats?.map((i) => {
        const price = i?.price ? (i?.price === "FREE" ? 0 : +i?.price) : 0;
        arr.push(price);
      });
    });
    const rawTotal =
      +state?.priceWithFare * 1 + arr.reduce((total, num) => total + num, 0);

    return roundPrice(rawTotal);
  };

  const getAllReversedSeats = () => {
    const arr = [];
    travelers?.map((traveler, index) => {
      reverseSeats[index][traveler?.id]?.seats?.map((i) => {
        if (i?.segmentId === seats[value]?.segmentId) {
          arr.push(i?.selectedSeat);
        }
      });
    });

    return arr;
  };

  useEffect(() => {
    if (activeStep === 1 && getUserFromLocalStorage()?.type === "MEMBER") {
      getMemberInfo.request();
    }
    if (activeStep === 2 && count === 0) {
      handelSeatMap();
    }
  }, [activeStep, count]);

  const resolveFareAdjustmentTotal = () => {
    const adj = state?.fareAdjustment;
    if (adj == null || adj === "" || adj === 0) return 0;
    if (typeof adj === "number") return Number.isFinite(adj) ? adj : 0;
    if (typeof adj === "string") {
      const parsed = Number(adj);
      return Number.isFinite(parsed) ? parsed : 0;
    }
    if (typeof adj === "object") {
      const parsed = Number(adj[state?.flight?.id] ?? 0);
      return Number.isFinite(parsed) ? parsed : 0;
    }
    return 0;
  };

  const travelerPricings = state?.flight?.travelerPricings || [];

  const getPriceForTraveler = (price) => {
    const count = travelerPricings.length || 1;
    const base = Number(price) || 0;
    return (base + resolveFareAdjustmentTotal() / count).toFixed(2);
  };

  const groupedTravelers = Object.entries(
    travelerPricings.reduce((acc, traveler) => {
      const type = traveler?.travelerType || "ADULT";
      if (!acc[type]) acc[type] = { count: 0, total: 0 };
      acc[type].count += 1;
      acc[type].total += Number(getPriceForTraveler(traveler?.price?.total)) || 0;
      return acc;
    }, {}),
  );

  const goBackToResults = () => {
    if (state?.prevPath) {
      navigate(state.prevPath);
      return;
    }
    navigate("/search");
  };

  return (
    <div className="reservation bkg-v2">
      <div className="reservation-content">
        <Box sx={{ width: "100%" }} className="reservation-column-stepper">
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label, index) => (
              <Step
                onClick={() => {
                  if (index === 0) {
                    goBackToResults();
                    return;
                  }
                  if (activeStep > index) {
                    setActiveStep(index);
                  }
                }}
                key={label}
              >
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        <main>
          {activeStep === 1 || activeStep === 3 ? (
            <header className="bk-s1-hero">
              {activeStep === 1 && (
                <button
                  type="button"
                  className="bk-back-results"
                  onClick={goBackToResults}
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
                  </svg>
                  {t("booking.actions.backToResults")}
                </button>
              )}
              <p className="bk-s1-hero-eyebrow">
                {activeStep === 1
                  ? t("booking.labels.step1Eyebrow")
                  : t("booking.labels.step4Eyebrow")}
              </p>
              <h1 className="heading-title">
                {state?.flightDetails?.from}
                <span className="bk-route-arrow">
                  <svg
                    viewBox="0 0 24 24"
                    preserveAspectRatio="xMidYMid meet"
                    aria-hidden="true"
                  >
                    <path d="M17.043 8.683a.588.588 0 01.852 0l2.922 2.921c.242.244.242.609 0 .792l-2.922 2.922a.554.554 0 01-.426.182c-.366 0-.609-.243-.609-.609V12.9L3.9 12.9a.9.9 0 01-.893-.787L3 12a.9.9 0 01.9-.9l12.96-.001v-1.99c0-.146.039-.253.117-.353l.066-.073z"></path>
                  </svg>
                </span>
                {state?.flightDetails?.to}
                {state?.flightDetails?.return === "Return" && (
                  <span className="bk-route-chip">
                    {t("booking.labels.return").toLowerCase()}
                  </span>
                )}
              </h1>
              <div className="bk-s1-hero-meta">
                {state?.flightDetails?.departureDate && (
                  <span>
                    {t("booking.labels.departure")}{" "}
                    {dayjs(state.flightDetails.departureDate)
                      .locale(locale)
                      .format("ddd D MMM")}
                  </span>
                )}
                {state?.flightDetails?.return === "Return" &&
                  state?.flightDetails?.returnDate && (
                    <span>
                      {t("booking.labels.return")}{" "}
                      {dayjs(state.flightDetails.returnDate)
                        .locale(locale)
                        .format("ddd D MMM")}
                    </span>
                  )}
                <span>
                  {state?.flight?.travelerPricings?.length || 0}{" "}
                  {(state?.flight?.travelerPricings?.length || 0) === 1
                    ? t("booking.labels.passenger")
                    : t("booking.labels.passengers")
                  }
                </span>
              </div>
              {state?.packageDeal && (
                <div className="bk-package-note">
                  {t("booking.labels.packageDealBooking", {
                    currency: currency?.sign,
                    price: Number(state?.packagePrice || 0).toLocaleString(),
                  })}
                </div>
              )}
            </header>
          ) : (
            <>
              <h1 className="heading-title">
                {state?.flightDetails?.from}
                <span className="bk-route-arrow">
                  <svg
                    viewBox="0 0 24 24"
                    preserveAspectRatio="xMidYMid meet"
                    aria-hidden="true"
                  >
                    <path d="M17.043 8.683a.588.588 0 01.852 0l2.922 2.921c.242.244.242.609 0 .792l-2.922 2.922a.554.554 0 01-.426.182c-.366 0-.609-.243-.609-.609V12.9L3.9 12.9a.9.9 0 01.9-.9l12.96-.001v-1.99c0-.146.039-.253.117-.353l.066-.073z"></path>
                  </svg>
                </span>
                {state?.flightDetails?.to}

                {state?.flightDetails?.return === "Return" && (
                  <span className="bk-route-chip">
                    {t("booking.labels.return").toLowerCase()}
                  </span>
                )}
              </h1>
              {state?.packageDeal && (
                <div className="bk-package-note">
                  {t("booking.labels.packageDealBooking", {
                    currency: currency?.sign,
                    price: Number(state?.packagePrice || 0).toLocaleString(),
                  })}
                </div>
              )}
            </>
          )}
          {activeStep === 1 && (
            <PassengerDetails
              state={state}
              travelersInit={travelersInit}
              validationSchema={validationSchema}
              handelInfoSubmit={handelInfoSubmit}
              setCode={setCode}
              source={state?.source}
              expiryAcknowledgedRef={expiryAcknowledgedRef}
            />
          )}
          {activeStep === 2 && (
            <SeatsMap
              loading={loading}
              errMsg={errMsg}
              value={value}
              travelers={travelers}
              seats={seats}
              reverseSeats={reverseSeats}
              handelRemoveSeat={handelRemoveSeat}
              handelTraverlsSeats={handelTraverlsSeats}
              getAllReversedSeats={getAllReversedSeats}
              handelSubmit={handelSubmit}
              setTravelerIndex={setTravelerIndex}
              handleChangeTab={handleChangeTab}
              state={state}
              travelerIndex={travelerIndex}
              currency={currency?.sign}
            />
          )}

          {activeStep === 3 && (
            <Overview
              overviewData={overviewData}
              state={state}
              travelers={travelers}
              paymentType={paymentType}
              handelReverseSubmit={handelReverseSubmit}
              setPaymentType={setPaymentType}
            />
          )}
        </main>
        <aside className="reversation-sideBar-wrapper">
          <div className="reversation-bill">
            <div className="reversation-bill-box bk-bill">
              <div className="bk-bill-head">
                <span className="bk-bill-sky" aria-hidden="true" />
                <div className="bk-bill-head-text">
                  <p>{t("booking.labels.priceSummary")}</p>
                  <span>
                    {state?.flight?.travelerPricings?.length || 0}{" "}
                    {(state?.flight?.travelerPricings?.length || 0) === 1
                      ? t("booking.labels.passenger")
                      : t("booking.labels.passengers")}
                  </span>
                </div>
              </div>
              <div className="reversation-bill-box-items">
                {groupedTravelers.map(([type, group]) => (
                  <div className="reversation-bill-item" key={type}>
                    <p>
                      {getTravelerTypeLabel(type)} ({group.count}x)
                    </p>
                    <p>
                      {currency?.sign}
                      {group.total.toFixed(2)}
                    </p>
                  </div>
                ))}
                {groupedTravelers.length === 0 && (
                  <div className="reversation-bill-item">
                    <p>{t("booking.labels.passengers")}</p>
                    <p>
                      {currency?.sign}
                      {roundPrice(state?.priceWithFare || 0).toFixed(2)}
                    </p>
                  </div>
                )}

                <div className="reversation-bill-item reversation-bill-item--tax">
                  <p>{t("booking.labels.taxesAndFees")}</p>
                  <p className="reversation-bill-included">
                    {t("booking.labels.included")}
                  </p>
                </div>

                {travelers?.length > 0 &&
                  reverseSeats?.length > 0 &&
                  travelers?.map(
                    (traveler, index) =>
                      state?.flight?.travelerPricings[index]?.travelerType !==
                        "HELD_INFANT" && (
                        <div className="reversation-bill-item" key={index}>
                          <p>
                            {t("booking.labels.seatFor", {
                              type: getTravelerTypeLabel(
                                state?.flight?.travelerPricings[index]
                                  ?.travelerType,
                              ),
                              name: `${traveler?.name?.firstName}.${traveler?.name?.lastName?.charAt(0)}`,
                            })}
                          </p>
                          <p>
                            {currency?.sign}
                            {reverseSeats[index][traveler?.id]?.seats
                              ?.map((i) =>
                                i?.price === "FREE" ? 0 : +i?.price,
                              )
                              ?.reduce((total, num) => total + num, 0)

                              .toFixed(2)
                              .toString()}
                          </p>
                        </div>
                      ),
                  )}

                {state?.packageDeal && state?.packagePrice != null && (
                  <div
                    className={`reversation-bill-item ${packagePriceBreakdown?.breakdown?.length ? "reversation-bill-item--breakdown" : ""}`}
                  >
                    <div>
                      <p>{t("booking.labels.packageDeal")}</p>
                      {packagePriceBreakdown?.breakdown?.length > 0 ? (
                        <div className="reversation-bill-package-breakdown">
                          {packagePriceBreakdown.breakdown.map((line, i) => {
                            const people = line.occupants * line.count;
                            const pricePerPersonStr =
                              line.pricePerPerson % 1 === 0
                                ? line.pricePerPerson.toString()
                                : line.pricePerPerson.toFixed(2);
                            const lineTotalStr =
                              line.lineTotal % 1 === 0
                                ? line.lineTotal.toString()
                                : line.lineTotal.toFixed(2);
                            return (
                              <p key={i} className="reversation-bill-room-line">
                                {getRoomTypeLabel(line.type) || line.label}:{" "}
                                {currency?.sign}
                                {pricePerPersonStr}/{t("booking.labels.person")} ×{" "}
                                {people}{" "}
                                {people === 1
                                  ? t("booking.labels.person")
                                  : t("booking.labels.people")}{" "}
                                ={" "}
                                {currency?.sign}
                                {lineTotalStr}
                              </p>
                            );
                          })}
                        </div>
                      ) : (
                        state?.packageRoomBreakdown && (
                          <p className="reversation-bill-room-breakdown">
                            {[
                              state.packageRoomBreakdown.single > 0 &&
                                `${state.packageRoomBreakdown.single}× ${getRoomTypeLabel("single")}`,
                              state.packageRoomBreakdown.double > 0 &&
                                `${state.packageRoomBreakdown.double}× ${getRoomTypeLabel("double")}`,
                              state.packageRoomBreakdown.triple > 0 &&
                                `${state.packageRoomBreakdown.triple}× ${getRoomTypeLabel("triple")}`,
                              state.packageRoomBreakdown.quad > 0 &&
                                `${state.packageRoomBreakdown.quad}× ${getRoomTypeLabel("quad")}`,
                            ]
                              .filter(Boolean)
                              .join(", ")}
                          </p>
                        )
                      )}
                    </div>
                    <p>
                      {currency?.sign}
                      {Number(state?.packagePrice).toFixed(2)}
                    </p>
                  </div>
                )}

              </div>

              <div className="bk-bill-total">
                <p>{t("booking.labels.total", { currency: currency?.currency })}</p>
                <p>
                  {currency?.sign}
                  {roundPrice(
                    (travelers?.length > 0 && reverseSeats?.length > 0
                      ? getTotalPrice()
                      : roundPrice(state?.priceWithFare || 0)) +
                      (state?.packageDeal && state?.packagePrice != null
                        ? roundPrice(state.packagePrice)
                        : 0),
                  ).toFixed(2)}
                </p>
              </div>
              <div className="bk-bill-trust">
                <div className="bk-bill-trust-row">
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z" />
                  </svg>
                  <p>{t("booking.labels.noHiddenCosts")}</p>
                </div>
                <div className="bk-bill-trust-row">
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z" />
                  </svg>
                  <p>{t("booking.labels.secureBooking")}</p>
                </div>
              </div>
            </div>
          </div>
        </aside>
      </div>
      <div className={`bk-pricebar ${expand ? "expand" : ""}`}>
        {expand && (
          <div className="bk-pricebar-panel">
            <div className="bk-pricebar-row">
              <p>{t("booking.labels.priceSummary")}</p>
              <p>
                {travelerPricings.length || 0}{" "}
                {(travelerPricings.length || 0) === 1
                  ? t("booking.labels.passenger")
                  : t("booking.labels.passengers")}
              </p>
            </div>
            {groupedTravelers.map(([type, group]) => (
              <div key={type} className="bk-pricebar-row">
                <p>
                  {getTravelerTypeLabel(type)} ({group.count}x)
                </p>
                <p>
                  {currency?.sign}
                  {group.total.toFixed(2)}
                </p>
              </div>
            ))}
            {groupedTravelers.length === 0 && (
              <div className="bk-pricebar-row">
                <p>{t("booking.labels.passengers")}</p>
                <p>
                  {currency?.sign}
                  {roundPrice(state?.priceWithFare || 0).toFixed(2)}
                </p>
              </div>
            )}
            <div className="bk-pricebar-row bk-pricebar-row--included">
              <p>{t("booking.labels.taxesAndFees")}</p>
              <p>{t("booking.labels.included")}</p>
            </div>
            {travelers?.length > 0 &&
              reverseSeats?.length > 0 &&
              travelers?.map(
                (traveler, index) =>
                  travelerPricings[index]?.travelerType !== "HELD_INFANT" && (
                    <div key={index} className="bk-pricebar-row">
                      <p>
                        {t("booking.labels.seatFor", {
                          type: getTravelerTypeLabel(
                            travelerPricings[index]?.travelerType,
                          ),
                          name: `${traveler?.name?.firstName}.${traveler?.name?.lastName?.charAt(0)}`,
                        })}
                      </p>
                      <p>
                        {currency?.sign}
                        {reverseSeats[index][traveler?.id]?.seats
                          ?.map((i) => (i?.price === "FREE" ? 0 : +i?.price))
                          ?.reduce((total, num) => total + num, 0)
                          .toFixed(2)
                          .toString()}
                      </p>
                    </div>
                  ),
              )}
            {state?.packageDeal && state?.packagePrice != null && (
              <div className="bk-pricebar-row">
                <p>{t("booking.labels.packageDeal")}</p>
                <p>
                  {currency?.sign}
                  {Number(state.packagePrice).toFixed(2)}
                </p>
              </div>
            )}
          </div>
        )}
        <button
          type="button"
          className="bk-pricebar-bar"
          onClick={() => setExpand(!expand)}
        >
          <span className="bk-pricebar-total">
            <span className="bk-pricebar-label">
              {t("booking.labels.total", { currency: currency?.currency })}
            </span>
            <span className="bk-pricebar-amount">
              {currency?.sign}
              {roundPrice(
                (travelers?.length > 0 && reverseSeats?.length > 0
                  ? getTotalPrice()
                  : roundPrice(state?.priceWithFare || 0)) +
                  (state?.packageDeal && state?.packagePrice != null
                    ? roundPrice(state.packagePrice)
                    : 0),
              ).toFixed(2)}
            </span>
          </span>
          <span className="bk-pricebar-chevron" aria-hidden="true">
            <svg viewBox="0 0 24 24">
              <path
                d="M6 14.5l6-5.5 6 5.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </button>
      </div>
      <Modal
        open={openPop}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <div className="pop-up-modal">
          {errModalMsg ? (
            <>
              <p className="main-title error-msg-modal">
                {t("booking.messages.reservationError")}
              </p>

              {errModalMsg?.map((err, index) => (
                <p style={{ marginTop: 4 }} key={index}>
                  {err?.title} :{err?.detail}
                </p>
              ))}
            </>
          ) : (
            <div className="reservation-loading-modal">
              <div className="reservation-loading-modal__icon">
                <span />
              </div>
              <p className="reservation-loading-modal__title">
                {t("booking.messages.reservationLoading")}
              </p>
              <p className="reservation-loading-modal__description">
                {t("booking.messages.reservationLoadingDescription")}
              </p>
              <LinearProgress
                className="reservation-loading-modal__progress"
                sx={{
                  backgroundColor: "rgba(241, 94, 34, 0.16)",
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: "var(--primary-color)",
                  },
                }}
              />
              <div className="reservation-loading-modal__tip">
                <p className="reservation-loading-modal__tip-title">
                  {t("booking.messages.reservationLoadingTipTitle")}
                </p>
                <p>{t("booking.messages.reservationLoadingTip")}</p>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};
export default Booking;
